import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { libelleZones } from "@shared/departements";
import { useResellers, useCreateReseller, useUpdateReseller, useDeleteReseller } from "@/hooks/use-inventory";
import { useCommercials } from "@/hooks/use-profiles";
import { ActionsLiees } from "@/components/actions-liees";
import { EntiteIntervenants } from "@/components/ContactLiens";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchSelect } from "@/components/search-select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/status-badge";
import { useLocation, useSearch } from "wouter";
import {
  Plus, Search, Store, Pencil, Trash2, Eye, Mail, Phone, TicketCheck,
  Building2, FileText, File, Upload, Download, Bot, ScrollText, X,
  TrendingUp, Target, Trophy, ShoppingCart, BarChart2, ExternalLink,
  Users, UserCheck, UserX, Send, ShieldCheck, Loader2, ZoomIn, Smartphone,
  Map as MapIcon, MapPin, Star, Navigation, CheckCircle2, Circle,
  AlertTriangle, PowerOff, ArrowRightLeft,
} from "lucide-react";
import { CopyButton } from "@/components/CopyButton";
import { useToast } from "@/hooks/use-toast";
import { ResellerFileExplorer } from "./FileExplorer";
import { useCanEditReseller } from "./sections";
import { useRevendeurContrats } from "@/hooks/use-reseller-contrats";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, Cell,
} from "recharts";
import type { Reseller, RevendeurContrat, RobotClient } from "@shared/schema";
import RevendeursMap from "@/components/resellers-map";
import AddressAutocomplete from "@/components/address-autocomplete";
import DepartementsSelector from "@/components/departements-selector";


const statusLabels: Record<string, string> = {
  revendeur: "Revendeur",
  revendeur_premium: "Revendeur Premium",
  sous_distributeur: "Sous-distributeur",
  apporteur_affaires: "Apporteur d'affaires",
  annule: "Annulé",
};

const contratStatutLabels: Record<string, string> = {
  en_cours: "En cours",
  expire: "Expiré",
  resilie: "Résilié",
};

function hasRenewalSoon(contrats: RevendeurContrat[] | undefined): boolean {
  if (!contrats) return false;
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return contrats.some(c => c.statut === "en_cours" && c.dateFin && new Date(c.dateFin) <= in30 && new Date(c.dateFin) >= now);
}

// ─── Helpers zones ───────────────────────────────────────────────────────────

function departementDepuisCP(cp?: string | null): string | null {
  const c = (cp ?? "").replace(/\s/g, "");
  if (!/^\d{4,5}$/.test(c)) return null;
  const cp5 = c.padStart(5, "0");
  if (cp5.startsWith("97") || cp5.startsWith("98")) return cp5.slice(0, 3);
  if (cp5.startsWith("20")) return parseInt(cp5, 10) < 20200 ? "2A" : "2B";
  return cp5.slice(0, 2);
}

function ZonesCell({
  departementSiege,
  departementsCouverts,
}: {
  departementSiege?: string | null;
  departementsCouverts?: string[];
}) {
  const couverts = departementsCouverts ?? [];
  const label = libelleZones(departementSiege ?? null, couverts);
  const detail = [
    departementSiege ? `Siège : ${departementSiege}` : null,
    couverts.length ? `Couverts : ${couverts.join(", ")}` : "Aucun département couvert",
  ]
    .filter(Boolean)
    .join(" — ");

  if (label === "—") return <span className="text-slate-500">—</span>;
  return (
    <span
      title={detail}
      className="inline-flex items-center rounded-full bg-slate-700/60 px-2 py-0.5 text-xs text-slate-100 cursor-default"
    >
      {label}
    </span>
  );
}

const emptyForm = {
  companyName: "",
  nomCommercial: "",
  siren: "",
  resellerNumber: "",
  puduAgentCode: "",
  sector: "",
  secteurActivite: "",
  anneesPartenariat: "",
  address: "",
  contactCivility: "" as string,
  contactFirstName: "",
  contactLastName: "",
  contactPhone: "",
  contactEmail: "",
  contractStatus: "revendeur" as string,
  contractStartDate: "",
  contractEndDate: "",
  annualSalesTarget: "",
  commercialId: "",
  notes: "",
  actif: true,
  logoUrl: "",
  siteWeb: "",
  zohoAliases: [] as string[],
  codeCourt: "",
};

function suggestCodeCourt(name: string): string {
  const cleaned = name
    .replace(/\b(SAS|SARL|SASU|SA|SNC|Groupe|Group|GmbH|LLC|Ltd|Inc|Le|La|Les)\b/gi, "")
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase();
  return cleaned.slice(0, 3);
}

async function fetchLogoFromSource(source: string): Promise<string | null> {
  if (!source) return null;
  let domain = "";
  if (source.includes("@")) {
    domain = source.split("@")[1] ?? "";
  } else {
    try {
      const u = new URL(source.startsWith("http") ? source : `https://${source}`);
      domain = u.hostname.replace(/^www\./, "");
    } catch {
      domain = source.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
    }
  }
  if (!domain) return null;
  try {
    const r = await authFetch(`/api/clearbit/logo?domain=${encodeURIComponent(domain)}`);
    const d = await r.json();
    return d.logoUrl ?? null;
  } catch {
    return null;
  }
}

export default function ResellerList() {
  const [, navigate] = useLocation();
  const qs = useSearch();
  const { data: resellersList, isLoading } = useResellers();
  const { data: commercialsList } = useCommercials();
  const _qsp = new URLSearchParams(qs);
  const [search, setSearch] = useState(() => _qsp.get("q") ?? "");
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    const raw = _qsp.get("inactifs");
    return raw === null ? true : raw !== "0";
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedResellerId, setSelectedResellerId] = useState<string | null>(null);
  const selectedReseller = useMemo(
    () => (resellersList ?? []).find((r: Reseller) => r.id === selectedResellerId) ?? null,
    [resellersList, selectedResellerId]
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resellerToDelete, setResellerToDelete] = useState<Reseller | null>(null);
  const [depLoading, setDepLoading] = useState(false);
  const [depData, setDepData] = useState<{ blocking: { key: string; label: string; count: number }[]; total: number } | null>(null);

  type RclPreviewItem = { cat: string; label: string; table: string; count: number };
  type RclPreview = {
    revendeur: { id: string; companyName: string; nomCommercial?: string };
    categories: RclPreviewItem[];
    aClasser: { table: string; col: string }[];
    totalBloquer: number;
    totalAClasser: number;
  };
  const [rclOpen, setRclOpen] = useState(false);
  const [rclTarget, setRclTarget] = useState<Reseller | null>(null);
  const [rclLoading, setRclLoading] = useState(false);
  const [rclPreview, setRclPreview] = useState<RclPreview | null>(null);
  const [rclNumero, setRclNumero] = useState("");
  const [rclSubmitting, setRclSubmitting] = useState(false);
  const { toast } = useToast();
  const createMutation = useCreateReseller();
  const updateMutation = useUpdateReseller();
  const deleteMutation = useDeleteReseller();

  const canEdit = useCanEditReseller();
  const commercials = commercialsList || [];
  const [activeTab, setActiveTab] = useState<"liste" | "carte" | "stats">(() => {
    const raw = _qsp.get("vue");
    return raw === "carte" || raw === "stats" ? raw : "liste";
  });
  const [editZones, setEditZones] = useState<string[]>([]);

  // ── Sync URL à chaque changement des 3 états de contexte liste ─────────────
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (!showInactive) params.set("inactifs", "0");
    if (activeTab !== "liste") params.set("vue", activeTab);
    const str = params.toString();
    navigate(str ? `/resellers?${str}` : "/resellers", { replace: true });
  }, [search, showInactive, activeTab]);

  const [formData, setFormData] = useState({ ...emptyForm });
  const [editData, setEditData] = useState({ ...emptyForm });
  const [formLogoUploading, setFormLogoUploading] = useState(false);
  const [aliasInput, setAliasInput] = useState("");

  const filteredResellers = (resellersList?.filter((r: Reseller) => {
    const q = search.toLowerCase();
    const matchesSearch = r.companyName.toLowerCase().includes(q) ||
      ((r as any).nomCommercial || '').toLowerCase().includes(q) ||
      (r.contactLastName || '').toLowerCase().includes(q) ||
      (r.contactFirstName || '').toLowerCase().includes(q);
    const matchesActive = showInactive || r.actif !== false;
    return matchesSearch && matchesActive;
  }) ?? []).sort((a: Reseller, b: Reseller) => {
    const na = (a as any).resellerNumber;
    const nb = (b as any).resellerNumber;
    if (!na && !nb) return 0;
    if (!na) return 1;
    if (!nb) return -1;
    return parseInt(nb, 10) - parseInt(na, 10);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        ...formData,
        annualSalesTarget: formData.annualSalesTarget ? parseInt(formData.annualSalesTarget) : null,
        anneesPartenariat: (formData as any).anneesPartenariat ? parseInt((formData as any).anneesPartenariat) : 1,
        contractStartDate: formData.contractStartDate || null,
        contractEndDate: formData.contractEndDate || null,
        commercialId: formData.commercialId || null,
        contactCivility: (formData.contactCivility as "M." | "Mme") || null,
        contractStatus: formData.contractStatus as any,
        actif: formData.actif,
        logoUrl: formData.logoUrl || null,
        siteWeb: (formData as any).siteWeb || null,
        puduAgentCode: (formData as any).puduAgentCode || null,
        codeCourt: (formData as any).codeCourt || null,
      } as any);
      setCreateOpen(false);
      toast({ title: "Revendeur ajouté", description: "Le revendeur a été créé avec succès" });
      setFormData({ ...emptyForm });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message || "Impossible de créer le revendeur" });
    }
  };

  const openEdit = (r: Reseller) => {
    setSelectedResellerId(r.id);
    setEditData({
      companyName: r.companyName,
      nomCommercial: (r as any).nomCommercial || "",
      siren: r.siren || "",
      resellerNumber: r.resellerNumber || "",
      puduAgentCode: (r as any).puduAgentCode || "",
      sector: r.sector || "",
      secteurActivite: (r as any).secteurActivite || "",
      anneesPartenariat: (r as any).anneesPartenariat?.toString() || "",
      address: r.address || "",
      contactCivility: r.contactCivility || "",
      contactFirstName: r.contactFirstName || "",
      contactLastName: r.contactLastName || "",
      contactPhone: r.contactPhone || "",
      contactEmail: r.contactEmail || "",
      contractStatus: r.contractStatus,
      contractStartDate: r.contractStartDate || "",
      contractEndDate: r.contractEndDate || "",
      annualSalesTarget: r.annualSalesTarget?.toString() || "",
      commercialId: r.commercialId || "",
      notes: r.notes || "",
      actif: r.actif !== false,
      logoUrl: r.logoUrl || "",
      siteWeb: (r as any).siteWeb || "",
      zohoAliases: (r as any).zohoAliases || [],
      codeCourt: (r as any).codeCourt || "",
    } as any);
    authFetch(`/api/resellers/${r.id}/zones`)
      .then((res) => res.json())
      .then((d) => setEditZones(d.departements || []))
      .catch(() => setEditZones([]));
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReseller || !selectedResellerId) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedResellerId,
        ...editData,
        annualSalesTarget: editData.annualSalesTarget ? parseInt(editData.annualSalesTarget) : null,
        anneesPartenariat: (editData as any).anneesPartenariat ? parseInt((editData as any).anneesPartenariat) : 1,
        contractStartDate: editData.contractStartDate || null,
        contractEndDate: editData.contractEndDate || null,
        commercialId: editData.commercialId || null,
        contactCivility: (editData.contactCivility as "M." | "Mme") || null,
        contractStatus: editData.contractStatus as any,
        actif: editData.actif,
        siteWeb: (editData as any).siteWeb || null,
        puduAgentCode: (editData as any).puduAgentCode || null,
        codeCourt: (editData as any).codeCourt || null,
      } as any);
      authFetch(`/api/resellers/${selectedResellerId}/zones`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ departements: editZones }),
      }).catch(() => {});
      setEditOpen(false);
      toast({ title: "Revendeur modifié", description: "Les modifications ont été enregistrées" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const confirmDelete = async (r: Reseller) => {
    setResellerToDelete(r);
    setDepData(null);
    setDepLoading(true);
    setDeleteConfirmOpen(true);
    try {
      const res = await authFetch(`/api/resellers/${r.id}/dependencies`);
      const data = await res.json();
      setDepData(data);
    } catch {
      setDepData({ blocking: [], total: 0 });
    } finally {
      setDepLoading(false);
    }
  };

  const confirmReclassify = async (r: Reseller) => {
    setRclTarget(r);
    setRclPreview(null);
    setRclNumero("");
    setRclLoading(true);
    setRclOpen(true);
    try {
      const res = await authFetch(`/api/resellers/${r.id}/reclassify-preview`);
      const data = await res.json();
      setRclPreview(data);
    } catch {
      setRclPreview(null);
    } finally {
      setRclLoading(false);
    }
  };

  const handleReclassify = async () => {
    if (!rclTarget || !rclNumero.trim()) return;
    setRclSubmitting(true);
    try {
      const res = await authFetch(`/api/resellers/${rclTarget.id}/reclassify-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numero_client: parseInt(rclNumero.trim(), 10) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur lors de la reclassification");
      toast({
        title: "Reclassification effectuée",
        description: `${(rclTarget as any).nomCommercial || rclTarget.companyName} est maintenant client N°${rclNumero}. ID : ${data.clientId}`,
      });
      setRclOpen(false);
      setRclTarget(null);
      queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Reclassification échouée", description: err.message });
    } finally {
      setRclSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!resellerToDelete) return;
    try {
      await updateMutation.mutateAsync({ id: resellerToDelete.id, actif: false } as any);
      toast({ title: "Revendeur désactivé", description: `${(resellerToDelete as any).nomCommercial || resellerToDelete.companyName} est maintenant inactif.` });
      setDeleteConfirmOpen(false);
      setResellerToDelete(null);
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const handleDelete = () => {
    if (!resellerToDelete) return;
    deleteMutation.mutate(resellerToDelete.id, {
      onSuccess: () => {
        toast({ title: "Revendeur supprimé", description: `${(resellerToDelete as any).nomCommercial || resellerToDelete.companyName} a été supprimé.` });
        setDeleteConfirmOpen(false);
        setResellerToDelete(null);
      },
      onError: (err: any) => {
        toast({ variant: "destructive", title: "Erreur", description: err.message });
      },
    });
  };

  const renderForm = (data: typeof formData, setData: typeof setFormData, onSubmit: (e: React.FormEvent) => void, isPending: boolean, submitLabel: string) => (
    <form onSubmit={onSubmit} className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto pr-2">
      {selectedReseller && (
        <div className="flex items-center gap-3 pb-2 border-b">
          <div className="relative group shrink-0">
            {(data as any).logoUrl ? (
              <img src={(data as any).logoUrl} alt="Logo" className="w-12 h-12 rounded-lg object-contain border bg-white" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center border">
                <Building2 className="w-6 h-6 text-purple-500" />
              </div>
            )}
            <label className="absolute inset-0 cursor-pointer rounded-lg opacity-0 group-hover:opacity-100 bg-black/40 flex items-center justify-center transition-opacity" title="Changer le logo">
              {formLogoUploading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Upload className="w-4 h-4 text-white" />}
              <input type="file" accept="image/*" className="hidden" disabled={formLogoUploading} onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                setFormLogoUploading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const res = await authFetch(`/api/resellers/${selectedResellerId}/logo`, { method: "POST", body: fd });
                  if (!res.ok) throw new Error("Erreur upload");
                  queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
                  toast({ title: "Logo mis à jour" });
                } catch { toast({ variant: "destructive", title: "Erreur upload logo" }); }
                finally { setFormLogoUploading(false); e.target.value = ""; }
              }} />
            </label>
          </div>
          <span className="text-sm text-muted-foreground">Survolez le logo pour le modifier</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Raison sociale *</Label>
          <Input data-testid="input-reseller-company" required value={data.companyName} onChange={e => setData({ ...data, companyName: e.target.value })} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label>Nom commercial</Label>
            <span
              title="Nom utilisé au quotidien (ex: Evotion pour la société EVEOZ SAS)"
              className="w-3.5 h-3.5 rounded-full bg-muted text-muted-foreground text-[9px] flex items-center justify-center cursor-help border border-border font-bold flex-shrink-0"
            >?</span>
          </div>
          <Input
            data-testid="input-reseller-nom-commercial"
            value={(data as any).nomCommercial || ""}
            onChange={e => setData({ ...data, nomCommercial: e.target.value } as any)}
            placeholder="Ex: Evotion"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SIREN</Label>
          <Input data-testid="input-reseller-siren" value={data.siren} onChange={e => setData({ ...data, siren: e.target.value })} placeholder="123 456 789" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>N° revendeur</Label>
          <Input data-testid="input-reseller-number" value={data.resellerNumber} onChange={e => setData({ ...data, resellerNumber: e.target.value })} placeholder="REV-001" />
        </div>
        <div className="space-y-2">
          <Label>Code agent Pudu</Label>
          <Input
            data-testid="input-pudu-agent-code"
            value={(data as any).puduAgentCode || ""}
            onChange={e => setData({ ...data, puduAgentCode: e.target.value } as any)}
            placeholder="FR0039"
            className="font-mono"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Code court <span className="text-muted-foreground font-normal text-xs">(3–5 lettres · ex: EVO, KARGO)</span></Label>
          <div className="flex gap-2">
            <Input
              data-testid="input-code-court"
              value={(data as any).codeCourt || ""}
              onChange={e => setData({ ...data, codeCourt: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5) } as any)}
              placeholder="EVO"
              className="font-mono uppercase"
              maxLength={5}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const suggested = suggestCodeCourt(data.companyName || "");
                if (suggested) setData({ ...data, codeCourt: suggested } as any);
              }}
              data-testid="button-suggest-code-court"
            >
              Suggérer
            </Button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Secteur d'activité</Label>
          <Input value={(data as any).secteurActivite || ""} onChange={e => setData({ ...data, secteurActivite: e.target.value } as any)} placeholder="Nettoyage, Robotique..." />
        </div>
        <div className="space-y-2">
          <Label>Ancienneté (années)</Label>
          <Input type="number" min="1" value={(data as any).anneesPartenariat || ""} onChange={e => setData({ ...data, anneesPartenariat: e.target.value } as any)} placeholder="1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Statut du contrat (legacy)</Label>
          <Select value={data.contractStatus} onValueChange={v => setData({ ...data, contractStatus: v })}>
            <SelectTrigger data-testid="select-contract-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="revendeur">Revendeur</SelectItem>
              <SelectItem value="revendeur_premium">Revendeur Premium</SelectItem>
              <SelectItem value="sous_distributeur">Sous-distributeur</SelectItem>
              <SelectItem value="apporteur_affaires">Apporteur d'affaires</SelectItem>
              <SelectItem value="annule">Annulé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Adresse</Label>
        <AddressAutocomplete
          value={data.address}
          placeholder="Commencez à taper l'adresse…"
          onChange={(v) => setData({ ...data, address: v, latitude: null, longitude: null, geocodingStatus: "pending" } as any)}
          onSelect={(sel) => setData({
            ...data,
            address: sel.label,
            latitude: sel.latitude,
            longitude: sel.longitude,
            geocodingStatus: "ok",
            geocodedAddress: sel.label,
            geocodingScore: sel.score,
            geocodingLabel: sel.label,
            departementSiege: sel.departement,
          } as any)}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Civilité</Label>
          <Select value={data.contactCivility} onValueChange={v => setData({ ...data, contactCivility: v })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M.">M.</SelectItem>
              <SelectItem value="Mme">Mme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Prénom</Label>
          <Input value={data.contactFirstName} onChange={e => setData({ ...data, contactFirstName: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Nom</Label>
          <Input value={data.contactLastName} onChange={e => setData({ ...data, contactLastName: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input value={data.contactPhone} onChange={e => setData({ ...data, contactPhone: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={data.contactEmail}
            onChange={e => setData({ ...data, contactEmail: e.target.value })}
            onBlur={async (e) => {
              if (!e.target.value || (data as any).logoUrl) return;
              const url = await fetchLogoFromSource(e.target.value);
              if (url) setData(prev => ({ ...prev, logoUrl: url }));
            }}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Site internet</Label>
        <div className="flex items-center gap-2">
          <Input
            type="url"
            placeholder="https://www.exemple.fr"
            value={(data as any).siteWeb || ""}
            onChange={e => setData({ ...data, siteWeb: e.target.value } as any)}
          />
          {(data as any).logoUrl ? (
            <img
              src={(data as any).logoUrl}
              alt="Logo"
              className="w-9 h-9 rounded object-contain border bg-white shrink-0"
              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 text-xs h-9"
            onClick={async () => {
              const source = (data as any).siteWeb || data.contactEmail;
              if (!source) return;
              const url = await fetchLogoFromSource(source);
              if (url) setData(prev => ({ ...prev, logoUrl: url } as any));
            }}
          >
            {(data as any).logoUrl ? "Re-fetch" : "Logo"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Début contrat</Label>
          <Input type="date" value={data.contractStartDate} onChange={e => setData({ ...data, contractStartDate: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Fin contrat</Label>
          <Input type="date" value={data.contractEndDate} onChange={e => setData({ ...data, contractEndDate: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Objectif ventes/an</Label>
          <Input type="number" value={data.annualSalesTarget} onChange={e => setData({ ...data, annualSalesTarget: e.target.value })} placeholder="0" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Commercial référent</Label>
        <SearchSelect
          data-testid="select-commercial"
          value={data.commercialId || null}
          onValueChange={v => setData({ ...data, commercialId: v || "" })}
          options={commercials.map((c: any) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
          placeholder="Sélectionner un commercial..."
          emptyText="Aucun commercial trouvé"
        />
      </div>
      <div className="flex items-center gap-3">
        <Label>Actif</Label>
        <Switch
          data-testid="switch-reseller-actif"
          checked={data.actif}
          onCheckedChange={v => setData({ ...data, actif: v })}
        />
      </div>
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          Noms alternatifs Zoho
          <span className="text-xs text-muted-foreground font-normal">(alias pour le matching commandes)</span>
        </Label>
        <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 border rounded-md bg-background focus-within:ring-1 focus-within:ring-ring">
          {((data as any).zohoAliases as string[]).map((alias, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-mono" data-testid={`badge-alias-${i}`}>
              {alias}
              <button
                type="button"
                onClick={() => setData({ ...data, zohoAliases: ((data as any).zohoAliases as string[]).filter((_: string, j: number) => j !== i) } as any)}
                className="ml-0.5 hover:text-red-500 transition-colors"
                data-testid={`btn-remove-alias-${i}`}
              >×</button>
            </span>
          ))}
          <input
            type="text"
            value={aliasInput}
            onChange={e => setAliasInput(e.target.value)}
            onKeyDown={e => {
              if ((e.key === "Enter" || e.key === ",") && aliasInput.trim()) {
                e.preventDefault();
                const newAlias = aliasInput.trim().replace(/,$/, "");
                if (newAlias && !((data as any).zohoAliases as string[]).includes(newAlias)) {
                  setData({ ...data, zohoAliases: [...((data as any).zohoAliases as string[]), newAlias] } as any);
                }
                setAliasInput("");
              } else if (e.key === "Backspace" && !aliasInput && ((data as any).zohoAliases as string[]).length > 0) {
                const aliases = (data as any).zohoAliases as string[];
                setData({ ...data, zohoAliases: aliases.slice(0, -1) } as any);
              }
            }}
            onBlur={() => {
              if (aliasInput.trim()) {
                const newAlias = aliasInput.trim();
                if (!((data as any).zohoAliases as string[]).includes(newAlias)) {
                  setData({ ...data, zohoAliases: [...((data as any).zohoAliases as string[]), newAlias] } as any);
                }
                setAliasInput("");
              }
            }}
            placeholder={((data as any).zohoAliases as string[]).length === 0 ? "Ex: EVEOZ, MonGroupe... (Entrée pour valider)" : "Ajouter un alias..."}
            className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
            data-testid="input-zoho-alias"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} rows={2} />
      </div>
      {selectedResellerId && (
        <div className="space-y-2 border-t pt-4">
          <Label>Départements couverts</Label>
          <p className="text-xs text-muted-foreground">Zones d'intervention commerciale (distinct du siège)</p>
          <DepartementsSelector value={editZones} onChange={setEditZones} />
        </div>
      )}
      <div className="pt-4 flex justify-end gap-2">
        <Button variant="outline" type="button" onClick={() => { setCreateOpen(false); setEditOpen(false); }}>Annuler</Button>
        <Button type="submit" disabled={isPending} data-testid="button-submit-reseller">
          {isPending ? "Enregistrement..." : submitLabel}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Revendeurs</h1>
          <p className="text-muted-foreground mt-1">Partenaires commerciaux et distributeurs</p>
        </div>
        {canEdit && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="shadow-lg shadow-primary/20" data-testid="button-add-reseller">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Revendeur
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px]">
              <DialogHeader>
                <DialogTitle>Nouveau Revendeur</DialogTitle>
                <DialogDescription>Ajouter un nouveau partenaire commercial.</DialogDescription>
              </DialogHeader>
              {renderForm(formData, setFormData, handleSubmit, createMutation.isPending, "Créer le revendeur")}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-xl w-fit border">
        <Button
          variant={activeTab === "liste" ? "default" : "ghost"}
          size="sm"
          className="rounded-lg h-8 text-xs"
          onClick={() => setActiveTab("liste")}
          data-testid="tab-liste"
        >
          <Store className="w-3.5 h-3.5 mr-1.5" />
          Liste
        </Button>
        <Button
          variant={activeTab === "carte" ? "default" : "ghost"}
          size="sm"
          className="rounded-lg h-8 text-xs"
          onClick={() => setActiveTab("carte")}
          data-testid="tab-carte"
        >
          <MapIcon className="w-3.5 h-3.5 mr-1.5" />
          Carte
        </Button>
        <Button
          variant={activeTab === "stats" ? "default" : "ghost"}
          size="sm"
          className="rounded-lg h-8 text-xs"
          onClick={() => setActiveTab("stats")}
          data-testid="tab-stats"
        >
          <BarChart2 className="w-3.5 h-3.5 mr-1.5" />
          Statistiques
        </Button>
      </div>

      {activeTab === "stats" ? (
        <StatistiquesTab />
      ) : activeTab === "carte" ? (
        <RevendeursMap
          onOpenReseller={(id) => {
            try { sessionStorage.setItem('resellers-list-qs', window.location.search); } catch {}
            navigate(`/resellers/${id}`);
          }}
        />
      ) : (
        <>
          <div className="flex items-center gap-4 bg-card p-2 rounded-xl border shadow-sm">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="input-search-resellers"
                placeholder="Rechercher par raison sociale ou contact..."
                className="pl-9 border-none bg-transparent focus-visible:ring-0"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pr-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Afficher les inactifs</Label>
              <Switch
                data-testid="switch-show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
            </div>
          </div>

          <div className="rounded-xl border bg-card shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead className="w-14 text-center font-mono">N°</TableHead>
                  <TableHead>Société</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead>Zones</TableHead>
                  <TableHead>Objectif</TableHead>
                  <TableHead className="text-right sticky right-0 z-20 bg-card before:content-[''] before:absolute before:inset-0 before:bg-secondary/40 before:pointer-events-none">
                    <span className="relative z-10">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={8} className="text-center h-32">Chargement...</TableCell></TableRow>
                ) : filteredResellers.map((r: Reseller) => (
                  <ResellerRow key={r.id} reseller={r} canEdit={canEdit} openEdit={openEdit} confirmDelete={confirmDelete} confirmReclassify={confirmReclassify} />
                ))}
                {!isLoading && filteredResellers.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aucun revendeur trouvé</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Modifier le revendeur</DialogTitle>
            <DialogDescription>Modifier les informations du revendeur.</DialogDescription>
          </DialogHeader>
          {renderForm(editData, setEditData, handleEdit, updateMutation.isPending, "Enregistrer")}
        </DialogContent>
      </Dialog>

      {/* ── Dialog Reclassification revendeur → client ── */}
      <Dialog open={rclOpen} onOpenChange={(o) => { if (!rclSubmitting) setRclOpen(o); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-blue-500" />
              Reclassifier en client
            </DialogTitle>
            <DialogDescription>
              <strong>{(rclTarget as any)?.nomCommercial || rclTarget?.companyName}</strong>
            </DialogDescription>
          </DialogHeader>

          {rclLoading ? (
            <div className="flex items-center justify-center py-10 gap-3 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Analyse des données…</span>
            </div>
          ) : !rclPreview ? (
            <div className="flex items-center justify-center py-10 gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">Impossible de charger l'inventaire.</span>
            </div>
          ) : rclPreview.totalAClasser > 0 ? (
            /* ── Tables FK inconnues — STOP ── */
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800 dark:text-red-300">
                  <p className="font-semibold mb-1">Tables FK non classifiées détectées (filet anti-régression)</p>
                  <p>Classez ces tables dans le code avant d'exécuter la reclassification.</p>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/30 divide-y text-sm">
                {rclPreview.aClasser.map(t => (
                  <div key={t.table} className="flex items-center justify-between px-3 py-2">
                    <span className="font-mono text-xs">{t.table}</span>
                    <span className="text-muted-foreground text-xs">col: {t.col}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-1">
                <Button variant="outline" onClick={() => setRclOpen(false)}>Fermer</Button>
              </div>
            </div>
          ) : rclPreview.totalBloquer > 0 ? (
            /* ── Sous-revendeurs bloquants ── */
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-semibold mb-1">Reclassification impossible</p>
                  <p>Ce revendeur a <strong>{rclPreview.totalBloquer} sous-revendeur(s) rattaché(s)</strong>. Réaffectez-les ou dissociez-les avant de reclassifier.</p>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/30 divide-y text-sm">
                {rclPreview.categories.filter(c => c.cat === 'BLOQUER').map(c => (
                  <div key={c.table} className="flex items-center justify-between px-3 py-2">
                    <span>{c.label}</span>
                    <span className="font-mono font-semibold tabular-nums">{c.count}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-1">
                <Button variant="outline" onClick={() => setRclOpen(false)}>Fermer</Button>
              </div>
            </div>
          ) : (
            /* ── Reclassification possible ── */
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Ce revendeur peut être reclassifié en client. L'inventaire ci-dessous résume les données concernées.
                </p>
              </div>

              {/* Tableau d'inventaire par catégorie */}
              <div className="rounded-lg border bg-muted/30 divide-y text-sm">
                {/* MIGRER */}
                {rclPreview.categories.filter(c => c.cat === 'MIGRER').map(c => (
                  <div key={c.table} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">MIGRER</span>
                      <span>{c.label}</span>
                    </div>
                    <span className="font-mono font-semibold tabular-nums">{c.count}</span>
                  </div>
                ))}
                {/* COPIER */}
                {rclPreview.categories.filter(c => c.cat === 'COPIER').map(c => (
                  <div key={c.table} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">COPIER</span>
                      <span>{c.label}</span>
                    </div>
                    <span className="font-mono font-semibold tabular-nums">{c.count}</span>
                  </div>
                ))}
                {/* RESTER avec count > 0 */}
                {rclPreview.categories.filter(c => c.cat === 'RESTER' && c.count > 0).map(c => (
                  <div key={c.table} className="flex items-center justify-between px-3 py-2 opacity-60">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">RESTER</span>
                      <span>{c.label}</span>
                    </div>
                    <span className="font-mono tabular-nums">{c.count}</span>
                  </div>
                ))}
              </div>

              {/* Input numéro client */}
              <div className="space-y-1.5">
                <Label htmlFor="rclNumero" className="text-sm font-medium">Numéro client à attribuer <span className="text-red-500">*</span></Label>
                <Input
                  id="rclNumero"
                  type="number"
                  min={1}
                  placeholder="ex. 1042"
                  value={rclNumero}
                  onChange={e => setRclNumero(e.target.value)}
                  className="w-full"
                  data-testid="input-reclassify-numero-client"
                />
                <p className="text-xs text-muted-foreground">Ce numéro sera le N° client de la nouvelle fiche. Il doit être unique.</p>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setRclOpen(false)} disabled={rclSubmitting} data-testid="button-cancel-reclassify">
                  Annuler
                </Button>
                <Button
                  onClick={handleReclassify}
                  disabled={rclSubmitting || !rclNumero.trim() || parseInt(rclNumero.trim(), 10) <= 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="button-confirm-reclassify"
                >
                  {rclSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Reclassification…</>
                  ) : (
                    <><ArrowRightLeft className="w-4 h-4 mr-1.5" />Reclassifier en client</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-500" />
              Supprimer le revendeur
            </DialogTitle>
            <DialogDescription>
              <strong>{(resellerToDelete as any)?.nomCommercial || resellerToDelete?.companyName}</strong>
            </DialogDescription>
          </DialogHeader>

          {depLoading ? (
            <div className="flex items-center justify-center py-8 gap-3 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Vérification des dépendances…</span>
            </div>
          ) : depData && depData.total > 0 ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Cette fiche est référencée par <strong>{depData.total} objet{depData.total > 1 ? 's' : ''}</strong> — la suppression est bloquée. Désactivez-la ou réaffectez ses liens avant de supprimer.
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 divide-y text-sm">
                {depData.blocking.map(dep => (
                  <div key={dep.key} className="flex items-center justify-between px-3 py-2">
                    <span className="text-foreground">{dep.label}</span>
                    <span className="font-mono font-semibold text-foreground tabular-nums">{dep.count}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="button-cancel-delete">Annuler</Button>
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30"
                  onClick={handleDeactivate}
                  disabled={updateMutation.isPending}
                  data-testid="button-deactivate-reseller"
                >
                  <PowerOff className="w-4 h-4 mr-1.5" />
                  {updateMutation.isPending ? "Désactivation…" : "Désactiver (actif = false)"}
                </Button>
              </div>
            </div>
          ) : depData && depData.total === 0 ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <p className="text-sm text-green-800 dark:text-green-300">
                  Aucune dépendance détectée — cette fiche peut être supprimée définitivement.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">Cette action est irréversible. Les données techniques associées (logs, tokens, notifications) seront supprimées automatiquement.</p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="button-cancel-delete">Annuler</Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  data-testid="button-confirm-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  {deleteMutation.isPending ? "Suppression…" : "Supprimer définitivement"}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResellerRow({ reseller: r, canEdit, openEdit, confirmDelete, confirmReclassify }: {
  reseller: Reseller;
  canEdit: boolean;
  openEdit: (r: Reseller) => void;
  confirmDelete: (r: Reseller) => void;
  confirmReclassify: (r: Reseller) => void;
}) {
  const [, navigate] = useLocation();
  const { data: contrats } = useRevendeurContrats(r.id);
  const renewal = hasRenewalSoon(contrats);
  const { data: allRes } = useResellers();
  const resellerById = useMemo(
    () => new Map((allRes ?? []).map((x: any) => [x.id, x])),
    [allRes]
  );
  const parentIdSet = useMemo(
    () => new Set((allRes ?? []).filter((x: any) => x.revendeurParentId).map((x: any) => x.revendeurParentId)),
    [allRes]
  );

  return (
    <TableRow className={`group hover:bg-secondary/20 transition-colors${r.actif === false ? " opacity-50" : ""}`} data-testid={`row-reseller-${r.id}`}>
      <TableCell className="text-center">
        {(r as any).resellerNumber ? (
          <span className="font-mono text-sm font-semibold text-foreground" data-testid={`text-numero-${r.id}`}>{(r as any).resellerNumber}</span>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 text-[10px] px-1.5 whitespace-nowrap" data-testid={`badge-a-numeroter-${r.id}`}>à numéroter</Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {r.logoUrl ? (
            <img src={r.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover border shadow-sm" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center border shadow-sm">
              <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">{(r as any).nomCommercial || r.companyName}</span>
              {(r as any).nomCommercial && r.companyName !== (r as any).nomCommercial && (
                <span className="text-[10px] text-muted-foreground font-normal">({r.companyName})</span>
              )}
              {(r as any).codeCourt && (
                <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 font-mono text-[10px] px-1.5 py-0" data-testid={`badge-code-court-${r.id}`}>
                  {(r as any).codeCourt}
                </Badge>
              )}
              {renewal && (
                <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800 text-[10px]" data-testid={`badge-renewal-${r.id}`}>
                  Renouvellement à venir
                </Badge>
              )}
              {!(r as any).typeRelation && (
                <Badge variant="outline" className="bg-slate-100 text-slate-500 border-dashed border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-600 text-[10px]" data-testid={`badge-aclasser-${r.id}`}>
                  À classer
                </Badge>
              )}
            </div>
            {(r as any).secteurActivite && <div className="text-xs text-muted-foreground">{(r as any).secteurActivite}</div>}
            {(() => {
              const parentR = resellerById.get((r as any).revendeurParentId ?? '');
              const isTete = parentIdSet.has(r.id);
              if (parentR) return <div className="text-xs text-violet-600 dark:text-violet-400">Groupe · {(parentR as any).nomCommercial || (parentR as any).companyName}</div>;
              if (isTete) return <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Centrale</div>;
              return null;
            })()}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          {(r.contactFirstName || r.contactLastName) && (
            <div className="text-sm font-medium">{r.contactCivility} {r.contactFirstName} {r.contactLastName}</div>
          )}
          {r.contactEmail && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Mail className="w-3 h-3" />{r.contactEmail}<CopyButton value={r.contactEmail} ariaLabel="Copier l'email" size="sm" /></div>
          )}
          {r.contactPhone && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Phone className="w-3 h-3" />{r.contactPhone}<CopyButton value={r.contactPhone} ariaLabel="Copier le numéro" size="sm" /></div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={r.contractStatus} type="default" label={statusLabels[r.contractStatus] || r.contractStatus} />
      </TableCell>
      <TableCell>
        {r.actif !== false ? (
          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" data-testid={`badge-actif-${r.id}`}>Actif</Badge>
        ) : (
          <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700" data-testid={`badge-inactif-${r.id}`}>Inactif</Badge>
        )}
      </TableCell>
      <TableCell>
        <ZonesCell departementSiege={(r as any).departementSiege} departementsCouverts={(r as any).departementsCouverts} />
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">{r.annualSalesTarget ?? '—'}</TableCell>
      <TableCell className="text-right sticky right-0 z-20 bg-card before:content-[''] before:absolute before:inset-0 before:bg-secondary/20 before:opacity-0 group-hover:before:opacity-100 before:pointer-events-none">
        <div className="relative z-10 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => { try { sessionStorage.setItem('resellers-list-qs', window.location.search); } catch {} navigate(`/resellers/${r.id}`); }} data-testid={`button-view-reseller-${r.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`button-edit-reseller-${r.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" title="Reclassifier en client" onClick={() => confirmReclassify(r)} data-testid={`button-reclassify-reseller-${r.id}`}>
              <ArrowRightLeft className="w-4 h-4 text-blue-500" />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => confirmDelete(r)} data-testid={`button-delete-reseller-${r.id}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

const SITE_TYPE_LABELS: Record<string, string> = {
  point_de_vente: "Point de vente",
  depot: "Dépôt",
  sav: "SAV",
  siege: "Siège",
  autre: "Autre",
};

const SITE_TYPE_COLORS: Record<string, string> = {
  point_de_vente: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  depot: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800",
  sav: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  siege: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
  autre: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
};

const emptySiteForm = {
  nom: "", type_site: "point_de_vente", livraison_adresse: "", livraison_code_postal: "",
  livraison_ville: "", livraison_pays: "France", contact_reception_nom: "",
  contact_reception_email: "", contact_reception_tel: "", livraison_horaires: "",
  livraison_rdv_obligatoire: false, livraison_notes: "", est_site_par_defaut: false,
  latitude: null as number | null,
  longitude: null as number | null,
  geocode_score: null as number | null,
};

function SitesSection({ resellerId, canEdit }: { resellerId: string; canEdit: boolean }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptySiteForm });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: sites = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ["/api/revendeurs", resellerId, "sites"],
    queryFn: async () => {
      const r = await authFetch(`/api/revendeurs/${resellerId}/sites`);
      if (!r.ok) throw new Error("Erreur chargement sites");
      return r.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      if (editingId) {
        const r = await authFetch(`/api/revendeur-sites/${editingId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!r.ok) { const e = await r.json(); throw new Error(e.message); }
        return r.json();
      } else {
        const r = await authFetch(`/api/revendeurs/${resellerId}/sites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!r.ok) { const e = await r.json(); throw new Error(e.message); }
        return r.json();
      }
    },
    onSuccess: () => {
      refetch();
      setDialogOpen(false);
      setEditingId(null);
      setForm({ ...emptySiteForm });
      toast({ title: editingId ? "Site mis à jour" : "Site créé" });
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Erreur", description: err.message }),
  });

  const defaultMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const r = await authFetch(`/api/revendeur-sites/${siteId}/defaut`, { method: "POST" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message); }
      return r.json();
    },
    onSuccess: () => { refetch(); toast({ title: "Site par défaut mis à jour" }); },
    onError: (err: any) => toast({ variant: "destructive", title: "Erreur", description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const r = await authFetch(`/api/revendeur-sites/${siteId}`, { method: "DELETE" });
      if (r.status === 204) return;
      const e = await r.json(); throw new Error(e.message);
    },
    onSuccess: () => { refetch(); setDeleteId(null); toast({ title: "Site supprimé" }); },
    onError: (err: any) => { setDeleteId(null); toast({ variant: "destructive", title: "Erreur", description: err.message }); },
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...emptySiteForm });
    setDialogOpen(true);
  }

  function openEdit(site: any) {
    setEditingId(site.id);
    setForm({
      nom: site.nom ?? "",
      type_site: site.typeSite ?? "point_de_vente",
      livraison_adresse: site.livraisonAdresse ?? "",
      livraison_code_postal: site.livraisonCodePostal ?? "",
      livraison_ville: site.livraisonVille ?? "",
      livraison_pays: site.livraisonPays ?? "France",
      contact_reception_nom: site.contactReceptionNom ?? "",
      contact_reception_email: site.contactReceptionEmail ?? "",
      contact_reception_tel: site.contactReceptionTel ?? "",
      livraison_horaires: site.livraisonHoraires ?? "",
      livraison_rdv_obligatoire: !!site.livraisonRdvObligatoire,
      livraison_notes: site.livraisonNotes ?? "",
      est_site_par_defaut: !!site.estSiteParDefaut,
      latitude: site.latitude ?? null,
      longitude: site.longitude ?? null,
      geocode_score: site.geocodeScore ?? null,
    });
    setDialogOpen(true);
  }

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Navigation className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Sites de livraison ({sites.length})</span>
        {canEdit && (
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={openNew} data-testid="button-add-site">
            <Plus className="w-3.5 h-3.5 mr-1" />Ajouter
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-xs py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Chargement…</div>
      ) : sites.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucun site enregistré</p>
      ) : (
        <div className="space-y-1.5">
          {sites.map((site: any) => (
            <div
              key={site.id}
              className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-muted/30 border border-border/40"
              data-testid={`site-card-${site.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{site.nom}</span>
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SITE_TYPE_COLORS[site.typeSite] ?? SITE_TYPE_COLORS.autre}`}>
                    {SITE_TYPE_LABELS[site.typeSite] ?? site.typeSite}
                  </Badge>
                  {site.estSiteParDefaut && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                      <Star className="w-2.5 h-2.5 mr-0.5 fill-current" />Défaut
                    </Badge>
                  )}
                  {site.geocodeStatut === "ok" ? (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                      <CheckCircle2 className="w-3 h-3" />géocodé
                    </span>
                  ) : site.geocodeStatut === "echec" ? (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
                      <Circle className="w-3 h-3" />non géocodé
                    </span>
                  ) : null}
                </div>
                {(site.livraisonAdresse || site.livraisonVille) && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {[site.livraisonAdresse, site.livraisonCodePostal, site.livraisonVille].filter(Boolean).join(", ")}
                  </div>
                )}
                {site.contactReceptionNom && (
                  <div className="text-xs text-muted-foreground">Contact : {site.contactReceptionNom}</div>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  {!site.estSiteParDefaut && (
                    <Button
                      variant="ghost" size="sm" className="h-7 w-7 p-0"
                      title="Définir comme site par défaut"
                      onClick={() => defaultMutation.mutate(site.id)}
                      disabled={defaultMutation.isPending}
                      data-testid={`button-site-defaut-${site.id}`}
                    >
                      <Star className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="sm" className="h-7 w-7 p-0"
                    onClick={() => openEdit(site)}
                    data-testid={`button-site-edit-${site.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(site.id)}
                    data-testid={`button-site-delete-${site.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog création / édition */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingId(null); setForm({ ...emptySiteForm }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier le site" : "Nouveau site de livraison"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs">Nom du site *</Label>
              <Input
                value={form.nom}
                onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                placeholder="ex: Agence Lyon"
                data-testid="input-site-nom"
              />
            </div>
            <div>
              <Label className="text-xs">Type de site</Label>
              <Select value={form.type_site} onValueChange={(v) => setForm((f) => ({ ...f, type_site: v }))}>
                <SelectTrigger data-testid="select-site-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="point_de_vente">Point de vente</SelectItem>
                  <SelectItem value="depot">Dépôt</SelectItem>
                  <SelectItem value="sav">SAV</SelectItem>
                  <SelectItem value="siege">Siège</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Adresse de livraison</Label>
                <AddressAutocomplete
                  value={form.livraison_adresse}
                  onChange={(v) => setForm((f) => ({ ...f, livraison_adresse: v, latitude: null, longitude: null, geocode_score: null }))}
                  onSelect={(sel) => setForm((f) => ({
                    ...f,
                    livraison_adresse: sel.voie,
                    livraison_code_postal: sel.code_postal,
                    livraison_ville: sel.ville,
                    latitude: sel.latitude,
                    longitude: sel.longitude,
                    geocode_score: sel.score,
                  }))}
                  placeholder="Saisir une adresse…"
                  data-testid="input-site-adresse"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Code postal</Label>
                  <Input
                    value={form.livraison_code_postal}
                    onChange={(e) => setForm((f) => ({ ...f, livraison_code_postal: e.target.value }))}
                    placeholder="Code postal"
                    data-testid="input-site-cp"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ville</Label>
                  <Input
                    value={form.livraison_ville}
                    onChange={(e) => setForm((f) => ({ ...f, livraison_ville: e.target.value }))}
                    placeholder="Ville"
                    data-testid="input-site-ville"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Contact réception</Label>
                <Input
                  value={form.contact_reception_nom}
                  onChange={(e) => setForm((f) => ({ ...f, contact_reception_nom: e.target.value }))}
                  placeholder="Nom"
                  data-testid="input-site-contact-nom"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.contact_reception_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_reception_email: e.target.value }))}
                  placeholder="email@…"
                  data-testid="input-site-contact-email"
                />
              </div>
              <div>
                <Label className="text-xs">Tél</Label>
                <Input
                  value={form.contact_reception_tel}
                  onChange={(e) => setForm((f) => ({ ...f, contact_reception_tel: e.target.value }))}
                  placeholder="06…"
                  data-testid="input-site-contact-tel"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Horaires de livraison</Label>
              <Input
                value={form.livraison_horaires}
                onChange={(e) => setForm((f) => ({ ...f, livraison_horaires: e.target.value }))}
                placeholder="Lun–Ven 9h–17h"
                data-testid="input-site-horaires"
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={form.livraison_notes}
                onChange={(e) => setForm((f) => ({ ...f, livraison_notes: e.target.value }))}
                rows={2}
                placeholder="Informations complémentaires…"
                data-testid="textarea-site-notes"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="rdv-obligatoire"
                checked={form.livraison_rdv_obligatoire}
                onChange={(e) => setForm((f) => ({ ...f, livraison_rdv_obligatoire: e.target.checked }))}
                className="rounded"
                data-testid="checkbox-site-rdv"
              />
              <Label htmlFor="rdv-obligatoire" className="text-xs cursor-pointer">RDV obligatoire pour livraison</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="site-defaut"
                checked={form.est_site_par_defaut}
                onChange={(e) => setForm((f) => ({ ...f, est_site_par_defaut: e.target.checked }))}
                className="rounded"
                data-testid="checkbox-site-defaut"
              />
              <Label htmlFor="site-defaut" className="text-xs cursor-pointer">Site par défaut</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={!form.nom || saveMutation.isPending}
              data-testid="button-site-save"
            >
              {saveMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              {editingId ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce site ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le site sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-site-delete-confirm"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────
interface ContratStat {
  resellerId: string;
  companyName: string;
  annee: number;
  objectifAnnuel: number;
  totalRobotsVendus: number;
  tauxAtteinte: number | null;
  statut: string | null;
}
interface CaStat {
  resellerId: string;
  caHt: number;
  nbCommandes: number;
}

function StatistiquesTab() {
  const { data, isLoading } = useQuery<{ contrats: ContratStat[]; caParReseller: CaStat[] }>({
    queryKey: ["/api/resellers/stats"],
    queryFn: async () => {
      const res = await authFetch("/api/resellers/stats");
      if (!res.ok) throw new Error("Failed to fetch reseller stats");
      return res.json();
    },
  });
  const [filterReseller, setFilterReseller] = useState<string>("all");

  if (isLoading) {
    return <div className="text-center py-16 text-muted-foreground">Chargement des statistiques…</div>;
  }

  const contrats: ContratStat[] = data?.contrats ?? [];
  const caMap = new Map((data?.caParReseller ?? []).map((c) => [c.resellerId, c]));

  // Latest contrat per reseller
  const latestMap = new Map<string, ContratStat>();
  for (const c of contrats) {
    const existing = latestMap.get(c.resellerId);
    if (!existing || c.annee > existing.annee) latestMap.set(c.resellerId, c);
  }
  const ranking = [...latestMap.values()]
    .filter((c) => (c.totalRobotsVendus ?? 0) > 0 || (c.objectifAnnuel ?? 0) > 0)
    .sort((a, b) => (b.totalRobotsVendus ?? 0) - (a.totalRobotsVendus ?? 0));

  const totalRobots = contrats.reduce((s, c) => s + (c.totalRobotsVendus ?? 0), 0);
  const totalCa = [...caMap.values()].reduce((s, c) => s + (c.caHt ?? 0), 0);
  const atteintCount = ranking.filter((c) => (c.tauxAtteinte ?? 0) >= 100).length;
  const best = ranking[0];

  const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
  const getBarFill = (taux: number) =>
    taux > 100 ? "#8b5cf6" : taux >= 80 ? "#10b981" : taux >= 50 ? "#f59e0b" : "#ef4444";
  const getTauxBadge = (taux: number) =>
    taux > 100
      ? "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400"
      : taux >= 80
      ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400"
      : taux >= 50
      ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400"
      : "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400";

  const getMedal = (i: number) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`);

  const barData = ranking.slice(0, 15).map((c) => ({
    name: c.companyName.length > 16 ? c.companyName.slice(0, 15) + "…" : c.companyName,
    vendus: c.totalRobotsVendus ?? 0,
    objectif: c.objectifAnnuel ?? 0,
    taux: c.tauxAtteinte ?? 0,
  }));

  // Multi-year line chart
  const multiAnnees = Array.from(new Set(contrats.map((c) => c.annee))).sort();
  const multiOptions = Array.from(
    new Map(contrats.map((c) => [c.resellerId, c.companyName])).entries()
  )
    .filter(([rid]) => contrats.filter((c) => c.resellerId === rid).length >= 2)
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedIds = filterReseller && filterReseller !== "all"
    ? [filterReseller]
    : multiOptions.slice(0, 6).map((o) => o.id);

  const lineChartData = multiAnnees.map((annee) => {
    const point: Record<string, string | number> = { annee: String(annee) };
    for (const rid of selectedIds) {
      const c = contrats.find((c) => c.resellerId === rid && c.annee === annee);
      const name = contrats.find((x) => x.resellerId === rid)?.companyName ?? rid;
      point[name] = c?.totalRobotsVendus ?? 0;
    }
    return point;
  });

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Bot className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Robots vendus (total)</span>
          </div>
          <p className="text-2xl font-bold" data-testid="kpi-total-robots">{totalRobots.toLocaleString("fr-FR")}</p>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-muted-foreground">CA total HT (Zoho)</span>
          </div>
          <p className="text-2xl font-bold" data-testid="kpi-total-ca">
            {totalCa > 0 ? `${Math.round(totalCa / 1000)} k€` : "—"}
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-orange-500" />
            <span className="text-xs text-muted-foreground">Objectifs atteints</span>
          </div>
          <p className="text-2xl font-bold">{atteintCount}</p>
          <p className="text-xs text-muted-foreground">sur {ranking.length} revendeurs actifs</p>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground">Meilleur revendeur</span>
          </div>
          <p className="text-base font-bold truncate">{best?.companyName ?? "—"}</p>
          {best && <p className="text-xs text-muted-foreground">{best.totalRobotsVendus} robots</p>}
        </div>
      </div>

      {/* Ranking table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-500" />
          <h3 className="font-semibold">Classement revendeurs (dernière année connue)</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Revendeur</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Robots vendus</TableHead>
              <TableHead className="text-right">Objectif</TableHead>
              <TableHead className="text-right">Atteinte</TableHead>
              <TableHead className="text-right">CA HT (Zoho)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ranking.slice(0, 25).map((c, i) => {
              const taux = c.tauxAtteinte ?? 0;
              const ca = caMap.get(c.resellerId);
              return (
                <TableRow key={c.resellerId} data-testid={`row-ranking-${c.resellerId}`}>
                  <TableCell className="font-bold text-base">{getMedal(i)}</TableCell>
                  <TableCell className="font-medium">{c.companyName}</TableCell>
                  <TableCell>{c.statut ? <Badge variant="outline" className="text-xs">{c.statut}</Badge> : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{c.totalRobotsVendus ?? 0}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{c.objectifAnnuel ?? 0}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={`text-xs ${getTauxBadge(taux)}`}>{taux.toFixed(1)} %</Badge>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground text-xs">
                    {ca ? `${ca.caHt.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €` : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Bar chart – atteinte des objectifs */}
      {barData.length > 0 && (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Atteinte des objectifs par revendeur</h3>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(240, barData.length * 28)}>
            <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 30, top: 4, bottom: 4 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
              <Tooltip
                formatter={(v: any, name: string) => [v, name === "vendus" ? "Robots vendus" : "Objectif"]}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="objectif" fill="#e2e8f0" name="Objectif" radius={[0, 2, 2, 0]} barSize={10} />
              <Bar dataKey="vendus" name="Vendus" radius={[0, 2, 2, 0]} barSize={10}>
                {barData.map((entry, i) => (
                  <Cell key={i} fill={getBarFill(entry.taux)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground justify-center">
            <span><span className="inline-block w-3 h-2 rounded bg-violet-500 mr-1" />Dépassé (&gt;100 %)</span>
            <span><span className="inline-block w-3 h-2 rounded bg-green-500 mr-1" />Atteint (≥80 %)</span>
            <span><span className="inline-block w-3 h-2 rounded bg-orange-400 mr-1" />Partiel (50–80 %)</span>
            <span><span className="inline-block w-3 h-2 rounded bg-red-400 mr-1" />Faible (&lt;50 %)</span>
          </div>
        </div>
      )}

      {/* Line chart – évolution multi-années */}
      {lineChartData.length > 1 && multiOptions.length > 0 && (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Évolution du volume par revendeur</h3>
            </div>
            <Select value={filterReseller} onValueChange={setFilterReseller}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="select-filter-reseller">
                <SelectValue placeholder="Top 6 multi-ans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Top 6 (automatique)</SelectItem>
                {multiOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={lineChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="annee" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {selectedIds.map((rid, idx) => {
                const name = contrats.find((c) => c.resellerId === rid)?.companyName ?? rid;
                return (
                  <Line
                    key={rid}
                    type="monotone"
                    dataKey={name}
                    stroke={COLORS[idx % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

